import { matchCount } from '@/utils/fixture-utils';
import { cn } from '@/lib/utils';
import type { CellModel } from './grid-model';
import type { DropVerdict } from './drop-state';

/**
 * How a cell presents itself, shared by the weekly matrix (`GridCell`) and the day view
 * (`GridDayView`). Both render the same `CellModel` in different layouts, so the *decisions* —
 * what the capacity reads as, which state paints which colour — belong here rather than being
 * written out in each renderer, where they drifted apart once already.
 *
 * Deliberately not in `grid-model.ts`: that file is a pure model with no knowledge of Tailwind
 * or of how anything looks. Deliberately not exported from `GridCell.tsx` either: a value export
 * living next to a component trips `react-refresh/only-export-components`.
 */

/**
 * No "/ N canchas" when the cell is undeclared: there is no N to divide by, and printing
 * "2 / 0 canchas" would read as an error rather than as "nobody declared this time".
 */
export function capacityLabel(cell: CellModel): string {
  return cell.courtsQuantity === null
    ? matchCount(cell.matches.length)
    : `${cell.matches.length} / ${cell.courtsQuantity} canchas`;
}

/**
 * Background and border for a cell, given its own state and the verdict for whatever is being
 * dragged over it. Returns only the state-dependent classes: the caller supplies its own layout
 * classes, which is the one thing the two views genuinely differ on.
 */
export function cellStateClasses(
  cell: CellModel,
  verdict: DropVerdict | null,
): string {
  return cn(
    cell.declared ? 'bg-white' : 'border-dashed bg-gray-50',
    cell.overbooked && 'border-red-400 bg-red-50',
    verdict?.state === 'ok' && 'bg-emerald-50 ring-2 ring-emerald-400',
    verdict?.state === 'warn' && 'bg-amber-50 ring-2 ring-amber-400',
  );
}

/** Text colour for the verdict's notes: amber when a pair can't play, muted otherwise. */
export function verdictReasonClass(verdict: DropVerdict): string {
  return verdict.state === 'warn' ? 'text-amber-700' : 'text-gray-600';
}
